import { useState, useEffect } from 'react';
import { FiSave, FiRefreshCw, FiTrash2, FiEdit, FiPlus, FiCopy, FiCloud, FiCloudOff } from 'react-icons/fi';
import useStore from '../store/useStore';
import profileService from '../services/profileService';
import { isSyncEnabled, ensureSyncCode } from '../services/syncService';
import './ProfileManager.css';
import { useToast } from './Toast';

/**
 * Profile Manager Component
 * Load, save, reload, duplicate, and delete profiles
 */

function ProfileManager() {
    const { currentProfile, profileModified, activeProfileSlot, setCurrentProfile, setProfileModified, switchProfileSlot } = useStore();
    const notify = useToast();

    const [profiles, setProfiles] = useState([]);
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [newProfileName, setNewProfileName] = useState('');
    const [syncCode, setSyncCode] = useState(null);

    // Load the install's sync code when cloud sync is configured.
    useEffect(() => {
        if (isSyncEnabled) {
            ensureSyncCode().then(setSyncCode);
        }
    }, []);

    // Load all profiles on mount
    useEffect(() => {
        loadProfiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProfiles = async () => {
        const loadedProfiles = await profileService.getAllProfiles();
        setProfiles(loadedProfiles);

        // Auto-load last used profile if available and we haven't selected one yet
        const lastUsedId = localStorage.getItem('lastUsedProfileId');
        if (lastUsedId && !selectedProfileId) {
            const lastProfile = loadedProfiles.find(p => p.id === lastUsedId);
            if (lastProfile) {
                setCurrentProfile({ ...lastProfile });
                setSelectedProfileId(lastUsedId);
            }
        }
    };

    const handleLoadProfile = async (profileId) => {
        // Loading a profile replaces the working copy — protect unsaved edits
        // the same way the slot switcher does.
        if (profileModified && !window.confirm('Load another profile?\n\nYour unsaved changes will be lost.')) {
            return;
        }

        // Handle "Default Setup" selection (empty value)
        if (!profileId) {
            const defaultProfile = profileService.createDefaultProfile();
            // Don't save it yet, just show it? Or should we?
            // User prefers "Select profile -> Load state".
            // If they pick "Default Setup", they get a fresh template.
            setCurrentProfile(defaultProfile);
            setSelectedProfileId(null);
            localStorage.removeItem('lastUsedProfileId'); // Clear last used
            setProfileModified(false);
            return;
        }

        const profile = profiles.find(p => p.id === profileId);
        if (profile) {
            setCurrentProfile({ ...profile }); // Clone to avoid mutation
            setSelectedProfileId(profileId);
            setProfileModified(false);
            localStorage.setItem('lastUsedProfileId', profileId); // Save preference
            console.log('✅ Loaded profile:', profile.name);
        }
    };

    const handleSave = async () => {
        if (!currentProfile) return;

        const savedProfile = await profileService.saveProfile(currentProfile);
        setCurrentProfile(savedProfile);
        setProfileModified(false);
        await loadProfiles();
    };

    const handleSaveAs = async () => {
        if (!newProfileName.trim()) return;

        const newProfile = {
            ...currentProfile,
            id: null, // New ID will be generated
            name: newProfileName.trim(),
            createdAt: null
        };

        const savedProfile = await profileService.saveProfile(newProfile);
        setCurrentProfile(savedProfile);
        setSelectedProfileId(savedProfile.id);
        setProfileModified(false);
        setShowSaveAsDialog(false);
        setNewProfileName('');
        await loadProfiles();
    };

    const handleReload = () => {
        if (!selectedProfileId) {
            // Reload default profile
            const defaultProfile = profileService.createDefaultProfile();
            setCurrentProfile(defaultProfile);
        } else {
            // Reload from saved
            const profile = profiles.find(p => p.id === selectedProfileId);
            if (profile) {
                setCurrentProfile({ ...profile });
            }
        }
        setProfileModified(false);
    };

    const handleDelete = async () => {
        if (!selectedProfileId) return;
        if (!confirm('Are you sure you want to delete this profile?')) return;

        // Optimistic UI update
        const prevProfiles = [...profiles];
        setProfiles(profiles.filter(p => p.id !== selectedProfileId));

        try {
            await profileService.deleteProfile(selectedProfileId);

            // If we deleted the current profile, switch to default or another one
            if (currentProfile && currentProfile.id === selectedProfileId) {
                const defaultProfile = profileService.createDefaultProfile();
                setCurrentProfile(defaultProfile);
                setSelectedProfileId(null);
                setProfileModified(false);
            }

            // Reload to ensure sync
            await loadProfiles();
        } catch (error) {
            console.error("Delete failed:", error);
            setProfiles(prevProfiles); // Revert on error
            notify("Failed to delete profile.", 'error');
        }
    };

    // "Clear All" / "Reset All" as requested
    const handleResetAll = async () => {
        if (!confirm('WARNING: This will DELETE ALL profiles and reset the app to a single fresh state.\n\nAre you sure you want to clear everything?')) return;

        try {
            // Delete all profiles
            for (const p of profiles) {
                await profileService.deleteProfile(p.id);
            }

            // Create one fresh default
            const newDefault = profileService.createDefaultProfile();
            const saved = await profileService.saveProfile(newDefault);

            // Reset state
            setProfiles([saved]);
            setCurrentProfile(saved);
            setSelectedProfileId(saved.id);
            setProfileModified(false);
            localStorage.removeItem('lastUsedProfileId');

            notify('All profiles cleared. Reset to factory default.', 'info');
        } catch (error) {
            console.error("Reset All failed:", error);
            notify("Failed to reset profiles.", 'error');
            await loadProfiles();
        }
    };

    const handleNewProfile = async () => {
        const defaultProfile = profileService.createDefaultProfile();
        // Save immediately to persist and get an ID if needed (though createDefault has one)
        const saved = await profileService.saveProfile(defaultProfile);

        // Refresh list so it appears in dropdown
        await loadProfiles();

        setCurrentProfile(saved);
        setSelectedProfileId(saved.id);
        setProfileModified(false);
    };

    const handleRename = async () => {
        if (!newProfileName.trim() || !currentProfile) return;

        const updated = {
            ...currentProfile,
            name: newProfileName.trim()
        };

        // Save first
        const saved = await profileService.saveProfile(updated);

        // RELOAD ALL profiles to refresh the list with the new name
        // This is crucial because the list comes from 'profiles' state
        await loadProfiles();

        // Set current profile to the saved one (ensure it has the ID)
        setCurrentProfile(saved);

        // Ensure dropdown is synced
        setSelectedProfileId(saved.id);

        setProfileModified(false);
        setShowRenameDialog(false);
        setNewProfileName('');
    };

    const openRenameDialog = () => {
        if (!currentProfile) return;
        setNewProfileName(currentProfile.name);
        setShowRenameDialog(true);
    };

    // Dialog keyboard support (a11y): Escape closes; Tab cycles inside the dialog.
    const handleModalKeyDown = (e, close) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            close();
            return;
        }
        if (e.key !== 'Tab') return;
        const focusables = e.currentTarget.querySelectorAll(
            'button, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    return (
        <div className="profile-manager glass">
            <div className="profile-header">
                <h3>📁 Profile Management</h3>
            </div>

            {/* Cloud Sync status */}
            <div className="sync-status-section" style={{ marginBottom: '20px', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#aaa', textTransform: 'uppercase' }}>
                    {isSyncEnabled ? <><FiCloud /> Cloud Sync</> : <><FiCloudOff /> Cloud Sync (off)</>}
                </h4>
                {isSyncEnabled ? (
                    syncCode ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="text-sm text-secondary">This device's sync code:</span>
                            <code style={{ fontSize: '0.75rem', userSelect: 'all' }}>{syncCode}</code>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(syncCode);
                                    notify('Sync code copied — paste it into your other device.', 'info');
                                }}
                                className="btn-secondary"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                                title="Copy sync code to clipboard"
                            >
                                <FiCopy /> Copy
                            </button>
                        </div>
                    ) : (
                        <p className="text-sm text-secondary">Generating sync code…</p>
                    )
                ) : (
                    <p className="text-sm text-secondary">
                        Profiles are stored in this browser only. Set VITE_SYNC_URL to enable multi-device sync.
                    </p>
                )}
            </div>

            {/* Profile Slot Switcher */}
            <div className="slot-switcher-section" style={{ marginBottom: '20px', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#aaa', textTransform: 'uppercase' }}>Active Profile Slot</h4>
                <div className="slot-switcher-buttons">
                    {[1, 2, 3].map(slot => (
                        <button
                            key={slot}
                            className={`btn-slot ${activeProfileSlot === slot ? 'active' : ''}`}
                            onClick={() => {
                                // Edits are synced into the old slot on switch, but
                                // confirm before swapping the working context mid-edit.
                                if (profileModified && slot !== activeProfileSlot &&
                                    !window.confirm(`Switch to Profile ${slot}?\n\nYour current edits stay in Profile ${activeProfileSlot} (unsaved).`)) {
                                    return;
                                }
                                switchProfileSlot(slot);
                            }}
                        >
                            PROFILE {slot}
                        </button>
                    ))}
                </div>
            </div>

            {/* Profile Selector */}
            <div className="profile-selector">
                <label htmlFor="profile-select">Current Profile:</label>
                <div className="profile-select-wrapper">
                    <select
                        id="profile-select"
                        value={selectedProfileId || ''}
                        onChange={(e) => handleLoadProfile(e.target.value)}
                        className="profile-select"
                    >
                        <option value="">Default Setup {!selectedProfileId && profileModified && '*'}</option>
                        {profiles.map(profile => (
                            <option key={profile.id} value={profile.id}>
                                {profile.name} {selectedProfileId === profile.id && profileModified ? '*' : ''}
                            </option>
                        ))}
                    </select>
                    {profileModified && <span className="modified-indicator">*</span>}
                </div>
            </div>

            {/* Actions */}
            <div className="profile-actions">
                <button
                    onClick={handleNewProfile}
                    className="btn-secondary"
                    title="Create new default profile"
                >
                    <FiPlus /> New
                </button>

                {profileModified && (
                    <button
                        onClick={handleSave}
                        className="btn-primary animate-fadeIn"
                        title="Save changes to current profile"
                    >
                        <FiSave /> Save
                    </button>
                )}

                <button
                    onClick={() => setShowSaveAsDialog(true)}
                    className="btn-secondary"
                    title="Save as new profile"
                >
                    <FiSave /> Save As
                </button>

                <button
                    onClick={openRenameDialog}
                    className="btn-secondary"
                    disabled={!currentProfile}
                    title="Rename current profile"
                >
                    <FiEdit /> Rename
                </button>

                {profileModified && (
                    <button
                        onClick={handleReload}
                        className="btn-secondary animate-fadeIn"
                        title="Discard changes"
                    >
                        <FiRefreshCw /> Revert
                    </button>
                )}

                <button
                    onClick={handleDelete}
                    className="btn-danger"
                    disabled={!selectedProfileId}
                    title="Delete current profile"
                >
                    <FiTrash2 /> Delete
                </button>

                <button
                    onClick={handleResetAll}
                    className="btn-warning"
                    title="Delete ALL profiles and start fresh"
                    style={{ border: '1px solid var(--color-warning)', color: 'var(--color-warning)' }}
                >
                    <FiTrash2 /> Reset All
                </button>
            </div>

            {/* Save As Dialog */}
            {showSaveAsDialog && (
                <div className="modal-overlay" onClick={() => setShowSaveAsDialog(false)}>
                    <div
                        className="modal-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="save-as-dialog-title"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => handleModalKeyDown(e, () => setShowSaveAsDialog(false))}
                    >
                        <h3 id="save-as-dialog-title">Save Profile As</h3>
                        <input
                            type="text"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            placeholder="Enter profile name..."
                            className="profile-name-input"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveAs();
                            }}
                        />
                        <div className="modal-actions">
                            <button onClick={handleSaveAs} className="btn-primary" disabled={!newProfileName.trim()}>
                                Save
                            </button>
                            <button onClick={() => setShowSaveAsDialog(false)} className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Dialog */}
            {showRenameDialog && (
                <div className="modal-overlay" onClick={() => setShowRenameDialog(false)}>
                    <div
                        className="modal-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="rename-dialog-title"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => handleModalKeyDown(e, () => setShowRenameDialog(false))}
                    >
                        <h3 id="rename-dialog-title">Rename Profile</h3>
                        <input
                            type="text"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            placeholder="Enter new profile name..."
                            className="profile-name-input"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRename();
                            }}
                        />
                        <div className="modal-actions">
                            <button onClick={handleRename} className="btn-primary" disabled={!newProfileName.trim()}>
                                Rename
                            </button>
                            <button onClick={() => setShowRenameDialog(false)} className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Info */}
            {currentProfile && (
                <div className="profile-info">
                    <p className="text-sm text-secondary">
                        {currentProfile.updatedAt
                            ? `Last saved: ${new Date(currentProfile.updatedAt).toLocaleString()}`
                            : 'Unsaved profile'}
                    </p>
                </div>
            )}
        </div>
    );
}

export default ProfileManager;
